import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Formik} from 'formik';
import {useHistory, useRouteMatch} from 'react-router-dom';
import Form_11View from './Form_11-view';
import {pathnames as P} from 'common/constants/pathnames';
import {shumaStatusTypeIds as STATUS} from '../../constants/shumaStatusTypeIds';
import {userTypeIds as USER_TYPE} from '../../../../common/constants/userTypeIds';
import {fileTypeId as T} from '../../constants/fileTypeId';
import {errors as E} from '../../../../common/constants/errors';
import {shumaType} from '../../../../common/constants/shumaType';
import {propertyTypesGroups as PT} from '../../constants/propertyTypesGroups';

const STEP_FULLY_FILLED_VALUE = 2;

const Form_11 = ({
                   getFileList,
                   uploadFile,
                   filesList,
                   loading,
                   stepData,
                   userTypeID = 1,
                   adminPermissionsLevel = 0,
                   setShumaStatusTypeId,
                   createFinalPDF,
                   sendForAdminApproval,
                   shumaTypeID,
                   showGlobalErrorMessage,
                   stepStatus,
                   propertyTypeID
                 }) => {
  const [calling, setCalling] = useState(false);
  const initialValues = {};
  const history = useHistory();
  const {params: {shumaId}} = useRouteMatch(P.INSPECTION_AND_FORM_IDS);
  const {
    shumaStatusTypeID,
    dangerousStructure,
    exceedBuilding,
    inspectionAtRisk
  } = stepData;

  const fileTypeIds = {
    additionalFile: T.ADDITIONAL_FILE,
  };

  const handleFileUpload = (fileTypeId, folderID) => async file => {
    setCalling(true);
    const filesArray = Boolean(file) ? Object.values(file) : [];
    const calls = filesArray.length > 0 && filesArray.map(async (item) => await submitFiles(fileTypeId, folderID)(item));
    const results = await Promise.all(calls);
    if (results) {
      updateList();
    }
    setCalling(false);
  };

  const submitFiles = (fileTypeId, folderID) => async fileToSave => {
    return await uploadFile({
      shumaId,
      filename: fileToSave.name,
      fileTypeId,
      fileToSave,
      folderID
    });
  };

  useEffect(() => {
    updateList();
  }, [shumaId]);

  const onFileDelete = () => {
    updateList();
  };

  const updateList = () => {
    getFileList({shumaId});
  };

  const checkStepStatusesAndCall = callback => {
    if (isAllStepsCompleted()) {
      return callback;
    } else {
      return showStepsNotFilledMessage;
    }
  }

  const isPropertyTypeLot = PT.LOT.includes(propertyTypeID);

  const isAllStepsCompleted = () => {
    const {
      step1_status,
      step3_status,
      step4_status,
      step5_status,
      step6_status,
      step9_status,
      step10_status,
      step12_status,
      step13_status,
    } = stepStatus;
    const commonSteps = {
      step1_status,
      step3_status,
      step4_status,
      step6_status,
      step13_status,
    }
    switch (shumaTypeID) {
      case shumaType.REGULAR:
        return checkStepsCompleted({...commonSteps, step5_status, step9_status, step10_status});
      case shumaType.BANK_FULL:
      case shumaType.BANK_SHORT:
        if (isPropertyTypeLot) {
          return checkStepsCompleted({...commonSteps, step12_status});
        } else {
          return checkStepsCompleted({...commonSteps, step5_status, step12_status});
        }
      default:
        return checkStepsCompleted({...commonSteps, step5_status, step9_status, step12_status});
    }
  };

  const checkStepsCompleted = steps => {
    return Object.values(steps).reduce((acc, value) => (value === STEP_FULLY_FILLED_VALUE) && acc, true);
  }

  const showStepsNotFilledMessage = () => {
    showGlobalErrorMessage(E.STEPS_NOT_FILLED);
  }

  const createPDFAndSetStatusCompleted = () => {
    const currentShumaStatusTypeID = shumaStatusTypeID;
    Promise.resolve()
      .then(() => setShumaStatusTypeId(STATUS.COMPLETED))
      .then(() => setCalling(true))
      .then(createPDF)
      .then(updateList)
      .catch(() => {
        showGlobalErrorMessage(E.CREATE_FINAL_PDF_ERROR)
        setShumaStatusTypeId(currentShumaStatusTypeID);
      })
      .finally(() => setCalling(false));
  };

  const sendAdminApprovalAndSetStatus = (status) => {
    const currentShumaStatusTypeID = shumaStatusTypeID;
    Promise.resolve()
      .then(() => setShumaStatusTypeId(status))
      .then(() => sendForAdminApproval({shumaID: shumaId}))
      .catch(() => setShumaStatusTypeId(currentShumaStatusTypeID));
  };

  const createPDF = async () => {
    const createdFileProps = await createFinalPDF({shumaID: shumaId});
    const {fileID, fileExtension} = createdFileProps;
    return {fileID, fileExtension};
  };

  const getApproveButtonProps = (shumaStatusTypeID, userTypeID, adminPermissionsLevel) => {
    if (shumaStatusTypeID === STATUS.COMPLETED || shumaStatusTypeID === STATUS.COMPLETED_MANUALLY) {
      return {
        disabled: false,
        label: 'הפקת דו"ח נוסף',
        onClick: checkStepStatusesAndCall(createPDFAndSetStatusCompleted),
      };
    }

    if (shumaStatusTypeID === STATUS.FROZEN) {
      return {
        disabled: true,
        label: 'מוקפא',
      };
    }

    if (userTypeID === USER_TYPE.REGULAR) {
      if (shumaStatusTypeID === STATUS.IN_PROGRESS || shumaStatusTypeID === STATUS.REJECTED_BY_ADMIN) {
        return {
          disabled: false,
          label: 'בקשה לאישור',
          onClick: checkStepStatusesAndCall(() => sendAdminApprovalAndSetStatus(STATUS.PENDING_ADMIN_REVIEW)),
        };
      }
      if (
        shumaStatusTypeID === STATUS.PENDING_ADMIN_REVIEW ||
        shumaStatusTypeID === STATUS.PENDING_REVIEW ||
        shumaStatusTypeID === STATUS.PENDING_SIGNATURE
      ) {
        return {
          disabled: true,
          label: 'ממתין לאישור',
        };
      }
      if (shumaStatusTypeID === STATUS.APPROVED_BY_ADMIN) {
        return {
          disabled: false,
          label: 'סיום הדו"ח',
          onClick: checkStepStatusesAndCall(createPDFAndSetStatusCompleted),
        };
      }
    }

    if (userTypeID === USER_TYPE.SENIOR || (userTypeID === USER_TYPE.ADMIN && adminPermissionsLevel === 0)) {
      if (shumaStatusTypeID === STATUS.PENDING_REVIEW || shumaStatusTypeID === STATUS.PENDING_SIGNATURE) {
        return {
          disabled: true,
          label: 'ממתין לאישור'
        }
      }
      if (
        shumaStatusTypeID !== STATUS.COMPLETED &&
        shumaStatusTypeID !== STATUS.COMPLETED_MANUALLY &&
        shumaStatusTypeID !== STATUS.PENDING_REVIEW &&
        shumaStatusTypeID !== STATUS.PENDING_SIGNATURE
      ) {
        if (dangerousStructure === 1 || exceedBuilding === 1 || Boolean(inspectionAtRisk)) {
          return {
            disabled: false,
            label: 'בקשה לאישור',
            onClick: checkStepStatusesAndCall(() => sendAdminApprovalAndSetStatus(STATUS.PENDING_REVIEW)),
          };
        } else {
          return {
            disabled: false,
            label: 'אישור וסיום',
            onClick: checkStepStatusesAndCall(createPDFAndSetStatusCompleted),
          };
        }
      }
    }

    if (userTypeID === USER_TYPE.ADMIN && adminPermissionsLevel === 1) {
      if (shumaStatusTypeID !== STATUS.COMPLETED && shumaStatusTypeID !== STATUS.COMPLETED_MANUALLY) {
        if (dangerousStructure === 1 || exceedBuilding === 1 || Boolean(inspectionAtRisk)) {
          return {
            disabled: false,
            label: 'בקשה לאישור',
            onClick: checkStepStatusesAndCall(() => sendAdminApprovalAndSetStatus(STATUS.PENDING_SIGNATURE)),
          };
        } else {
          return {
            disabled: false,
            label: 'אישור וסיום',
            onClick: checkStepStatusesAndCall(createPDFAndSetStatusCompleted),
          };
        }
      }
    }

    if (userTypeID === USER_TYPE.ADMIN && adminPermissionsLevel === 2) {
      if (shumaStatusTypeID !== STATUS.COMPLETED && shumaStatusTypeID !== STATUS.COMPLETED_MANUALLY) {
        return {
          disabled: false,
          label: 'אישור וסיום',
          onClick: checkStepStatusesAndCall(createPDFAndSetStatusCompleted),
        };
      }
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => {}}
      enableReinitialize={true}
      validateOnChange={true}
      validateOnBlur={false}
    >
      {(formikProps) => (
        <Form_11View
          goToPrevPage={() => history.push(P.GET_FORM_BY_ID_AND_STEP(shumaId, 8))}
          files={filesList}
          onFileDelete={onFileDelete}
          onFileSelected={handleFileUpload}
          fileTypeIds={fileTypeIds}
          loading={loading}
          calling={calling}
          approveButtonProps={getApproveButtonProps(shumaStatusTypeID, userTypeID, adminPermissionsLevel)}
          shumaTypeID={shumaTypeID}
          {...formikProps}
        />
      )}
    </Formik>
  );
};

Form_11.propTypes = {
  getFileList: PropTypes.func,
  uploadFile: PropTypes.func,
  filesList: PropTypes.array,
  loading: PropTypes.bool,
};

export default Form_11;
